/**
 * Email-verification wall.
 *
 * Protects the main app shell. An account whose email is still unverified is
 * allowed in during a grace window after sign-up (so a brand-new user can reach
 * the app while their verification mail is in flight — the persistent banner +
 * the send-gate already nag them). Once the grace window has elapsed, the same
 * unverified account is HARD-BLOCKED: every shell navigation is redirected to
 * the standalone `/verify-required` page until the address is confirmed.
 *
 * Safety rails:
 *  - Gated behind `environment.features.enforceEmailVerificationWall`
 *    (default OFF). Ship dormant; enable only once the verification e-mail flow
 *    is confirmed working in the target environment, or a real user could be
 *    locked out with no way to verify.
 *  - Before walling someone off we force ONE fresh `reload()` of the Firebase
 *    user — the cached `emailVerified` lags (a user who just clicked the link in
 *    another tab still carries a stale token), and we must never block someone
 *    who is actually verified.
 *  - Fail-open: any error/timeout lets the user through. A locked-out user is a
 *    worse failure than a briefly-unenforced wall.
 *
 * Follows the same shape as `OnboardingCompletedGuard`: wait for the user doc to
 * hydrate (`AuthStore.userLoaded`), `take(1)`, map to a `UrlTree` or `true`.
 */

import { Injectable, inject } from '@angular/core';
import { CanActivate, UrlTree, Router } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  Observable} from 'rxjs';
import {
  catchError,
  filter,
  from,
  map,
  of,
  switchMap,
  take,
  timeout,
} from 'rxjs';

import { AuthStore } from '@core/services/auth/auth.store';
import { AuthService } from '@core/services/auth/auth.service';
import { environment } from 'environments/environment';

const WAIT_MS = 10000;
const DEFAULT_GRACE_HOURS = 48;
const HOUR_MS = 60 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class EmailVerificationRequiredGuard implements CanActivate {
  private readonly router = inject(Router);
  private readonly authStore = inject(AuthStore);
  private readonly authService = inject(AuthService);
  private readonly userLoaded$ = toObservable(this.authStore.userLoaded);

  canActivate(): Observable<boolean | UrlTree> {
    // Wall disabled for this environment → never interfere.
    if (!environment.features?.enforceEmailVerificationWall) {
      return of(true);
    }

    return this.userLoaded$.pipe(
      // Never decide on a not-yet-hydrated user (see OnboardingCompletedGuard).
      filter((loaded) => loaded),
      take(1),
      timeout(WAIT_MS),
      switchMap(() => {
        // Already verified → always allowed.
        if (this.authStore.emailVerified()) {
          return of<boolean | UrlTree>(true);
        }

        // Still inside the post-signup grace window → allow (banner only). We
        // deliberately do NOT block the first sessions.
        const ageMs = this.accountAgeMs();
        if (ageMs === null || ageMs < this.graceMs()) {
          return of<boolean | UrlTree>(true);
        }

        // Past the grace window and unverified in our cached state. Force one
        // fresh reload before walling them off — never block a user who has, in
        // fact, just verified in another tab.
        return from(
          this.authService.refreshEmailVerificationStatus().catch(() => false),
        ).pipe(
          map((verified) =>
            verified ? true : this.router.createUrlTree(['/verify-required']),
          ),
        );
      }),
      catchError((err) => {
        // Fail open: an infra hiccup must never lock a user out of their own
        // account. The banner + send-gate still push them to verify.
        console.error('[EmailVerificationRequiredGuard] error/timeout:', err);
        return of<boolean | UrlTree>(true);
      }),
    );
  }

  /** Configured grace window in milliseconds. */
  private graceMs(): number {
    const hours = environment.features?.emailVerificationGraceHours;
    return (typeof hours === 'number' && hours >= 0 ? hours : DEFAULT_GRACE_HOURS) * HOUR_MS;
  }

  /**
   * Milliseconds since the account was created, or null if unknown. Prefers the
   * Firebase Auth `metadata.creationTime` (server-set, always present) and falls
   * back to the Firestore `users/{uid}.createdAt`.
   */
  private accountAgeMs(): number | null {
    const creationTime =
      this.authStore.getCurrentFirebaseUser()?.metadata?.creationTime;
    if (creationTime) {
      const created = new Date(creationTime).getTime();
      if (!Number.isNaN(created)) {
        return Date.now() - created;
      }
    }

    const createdAt = this.authStore.user()?.createdAt;
    if (createdAt instanceof Date && !Number.isNaN(createdAt.getTime())) {
      return Date.now() - createdAt.getTime();
    }

    return null;
  }
}
